import { ColumnType } from './ColumnTypes';
import { AlignmentType } from './Alignment';
import * as React from 'react';
import * as Classes from '../common/classes';
import { TextCell } from './TextCell';
import { IntegerCell } from './IntegerCell';
import { DecimalCell } from './DecimalCell';
import { DateCell } from './DateCell';
import { DateTimeCell } from './DateTimeCell';
import { CurrencyCell } from './CurrencyCell';
import { CurrencyDecimalCell } from './CurrencyDecimalCell';
import classNames from 'classnames';
import { CSSProperties, ReactNode } from 'react';
import { LinkProps } from 'react-router-dom';
import { BooleanCell } from './BooleanCell';
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  formatNumber,
} from '@mc/react-format';
import { FilterDef } from '../filter/Filters';
import { DeepPartial } from '../common/props';

export type GetRowPropsType<DATA> = (props: {
  row: DATA | null | undefined;
  rowIndex: number;
}) =>
  | {
      /**
       * Pokud se má  vyrenderovat jako link (a href), předá se tato properta a kždý řádek bude jako a href.
       */
      link?: LinkProps;
      onClick?: (
        event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
      ) => any;
      className?: string;
      style?: CSSProperties;
    }
  | undefined;

export type TableCellProps<DATA extends object, VALUE extends unknown> = {
  rowIndex: number;
  columnIndex: number;
  row: DATA;
  column: TableColumn<DATA>;
  /**
   * Je k dispozici jen u taulek, které mají data na klientovi, např. základní Table
   */
  rows?: DATA[];
  value?: VALUE;
  formattedValue?: VALUE;
};
export type TableFooterCellProps<
  DATA_FOOTER extends object,
  VALUE extends unknown
> = {
  footerData?: DeepPartial<DATA_FOOTER>;
  /**
   * Je k dispozici jen u taulek, které mají data na klientovi, např. základní Table
   */
  value?: VALUE;
  formattedValue?: VALUE;
};
export type TableFooterProps<DATA extends object> = {
  /**
   * Je k dispozici jen u taulek, které mají data na klientovi, např. základní Table
   */
  rows?: DATA[];
};

export type TableCellRender<D extends object = {}, VALUE extends unknown = {}> =
  (props: TableCellProps<D, VALUE>) => React.ReactNode | string;

export type TitleCellRender<D extends object = {}, VALUE extends unknown = {}> =
  (props: TableCellProps<D, VALUE>) => string | React.ReactNode;

export type TableFooterCellRender<
  DATA_FOOTER extends object = {},
  VALUE extends unknown = {}
> = (
  props: TableFooterCellProps<DATA_FOOTER, VALUE>
) => React.ReactNode | string;

export type TableColumnSortProps<
  DATA extends object,
  VALUE extends unknown = {}
> = {
  rowA: DATA;
  valueA?: VALUE;
  rowB: DATA;
  valueB?: VALUE;
};
export type TimeProps = {
  withSeconds?: boolean;
};
export type NumberProps = {
  fractionDigits?: number;
  noThousandsSeparator?: boolean;
};
export type TableColumnSort<D extends object = {}, VALUE extends unknown = {}> =
  (props: TableColumnSortProps<D, VALUE>) => number;
export type FooterProps<DATA_FOOTER extends object = {}> = {
  id?: string;
  accessor?: string;
  type?: ColumnType;
  cell?: TableFooterCellRender<DATA_FOOTER>;
  noThousandsSeparator?: boolean;
  timeProps?: TimeProps;
  fractionDigits?: number;
};
export type TableFooterRender<D extends object = {}> = (
  props: TableFooterProps<D>
) => React.ReactNode | string | undefined;

/**
 * @deprecated použít ClassicTableColumn s velkým 'C'
 */
export type classicTableColumn<D extends object = {}> = ClassicTableColumn<D>;

export type ClassicTableColumn<D extends object = {}> = {
  header?: string | ReactNode;
  /**
   * Cesta k načtení dat, může být použit i zápis např. 'adresa.ulice'. Hodnota se používá i pro sort. Název sort lze přetížit uvedením stributu id.
   */
  accessor?: string;
  /**
   * Název atributu, používá se např. u sort (když se řadí dle tohoto sloupce, pošle se jako sort hodnota accessor, pokud je uvedeno id, tak se použije hodnota s id, jedná se o případ, kdy do accessor např. potřebujeme ulice.nazev).
   */
  id?: string;
  customSort?: TableColumnSort<D>;
  type?: ColumnType;
  /**
   * Pokud je sloupec typu akce, tak zde mje počet akčních tlačítek - berou se v ůvahu POUZE malá tlačítka typu icon a předpokládá se, že jsou v ButtonToolbar (pokud je jich více než jedno). Výchozí hodnota je 1.
   */
  actionSize?: number;
  filters?: FilterDef[];
  align?: AlignmentType;
  timeProps?: TimeProps;
  fractionDigits?: number;
  noThousandsSeparator?: boolean;
  className?: string;
  cell?: TableCellRender<D>;
  footer?: TableFooterRender<D> | string;
  width?: number | string;
  sortable?: boolean;
};

export type TableColumn<
  D extends object = {},
  DATA_FOOTER extends object = {}
> = {
  header?: string | ReactNode;
  /**
   * Cesta k načtení dat, může být použit i zápis např. 'adresa.ulice'. Hodnota se používá i pro sort. Název sort lze přetížit uvedením stributu id.
   */
  accessor?: string;
  /**
   * Název atributu, používá se např. u sort (když se řadí dle tohoto sloupce, pošle se jako sort hodnota accessor, pokud je uvedeno id, tak se použije hodnota s id, jedná se o případ, kdy do accessor např. potřebujeme ulice.nazev).
   */
  id?: string;
  customSort?: TableColumnSort<D>;
  type?: ColumnType;
  /**
   * Pokud je sloupec typu akce, tak zde mje počet akčních tlačítek - berou se v ůvahu POUZE malá tlačítka typu icon a předpokládá se, že jsou v ButtonToolbar (pokud je jich více než jedno). Výchozí hodnota je 1.
   */
  actionSize?: number;
  filters?: FilterDef[];
  align?: AlignmentType;
  fractionDigits?: number;
  //TODO MCW-6140 nahradit fractionDights,noThousandsSeparator... za NumberProps
  noThousandsSeparator?: boolean;
  timeProps?: TimeProps;
  className?: string;
  cell?: TableCellRender<D>;
  title?: TitleCellRender<D>;
  //footer?: TableFooterRender<D> | string;
  footer?: FooterProps<DATA_FOOTER>;
  width?: number | string;
  sortable?: boolean;
  columnTitle?: string;
};

export type BaseTableProps<D extends object = {}> = {
  columns: TableColumn<D>[];
  className?: string;
  /**
   * Vypne hover, spuštěný automaticky na řádcích s onClick.
   */
  rowDisableHover?: boolean;
  getRowProps?: GetRowPropsType<D>;
};
export type ClassicBaseTableProps<D extends object = {}> = {
  columns: ClassicTableColumn<D>[];
  className?: string;
  /**
   * Vypne hover, spuštěný automaticky na řádcích s onClick.
   */
  rowDisableHover?: boolean;
  getRowProps?: GetRowPropsType<D>;
};

export const getFormattedColumns = <D extends object = {}>(
  columns: TableColumn<D>[]
) => {
  return columns.map((column) => {
    const alignClass = classNames(
      column.align
        ? getTableAligment(column.align)
        : getTableAligmentByType(column?.type) || Classes.TABLE_ALIGN_LEFT,
      column.className
    );
    const { className, cell, ...rest } = column;

    // @ts-ignore
    const formatedColumn: TableColumn<D> = {
      ...rest,
      // @ts-ignore
      className: alignClass,
      // @ts-ignore
      ...((column.cell || column.type) && {
        // @ts-ignore
        cell: column.cell ? column.cell : getCell(column.type),
      }),
      ...((column?.footer?.cell || column?.footer?.type) && {
        // @ts-ignore
        footer: {
          ...column.footer,
          cell: column?.footer?.cell
            ? column?.footer?.cell
            : getCell(column.footer?.type),
        },
      }),
    };
    return formatedColumn;
  });
};

export const getTableAligment = (
  aligment: AlignmentType
): string | undefined => {
  switch (aligment) {
    case 'center':
      return Classes.TABLE_ALIGN_CENTER;
    case 'right':
      return Classes.TABLE_ALIGN_RIGHT;
    case 'left':
      return Classes.TABLE_ALIGN_LEFT;
    default:
      return undefined;
  }
};

export const getTableAligmentByType = (
  type?: ColumnType
): string | undefined => {
  if (!type) {
    return '';
  }

  switch (type) {
    case 'integer':
    case 'decimal':
    case 'currency':
    case 'currencyDecimal':
      return Classes.TABLE_ALIGN_RIGHT;
    default:
      return undefined;
  }
};

export const getCell = (type?: ColumnType) => {
  switch (type) {
    case 'text':
      return TextCell;
    case 'integer':
      return IntegerCell;
    case 'decimal':
      return DecimalCell;
    case 'date':
      return DateCell;
    case 'dateTime':
      return DateTimeCell;
    case 'currency':
      return CurrencyCell;
    case 'currencyDecimal':
      return CurrencyDecimalCell;
    case 'boolean':
      return BooleanCell;
    default:
      return undefined;
  }
};
export const getFormattedValue = <D extends object, DATA_FOOTER extends object>(
  columnOrFooter:
    | TableColumn<D>
    | FooterProps<DATA_FOOTER>
    | ClassicTableColumn<D>,
  value: any
) => {
  //In code's cold embrace, I seek solace, yet find none; my soul, undefined.
  switch (columnOrFooter.type) {
    case 'text':
      return value;
    case 'integer':
      return columnOrFooter?.noThousandsSeparator
        ? value
        : value !== null && value !== undefined && formatNumber(value);
    case 'decimal':
      return value && formatNumber(value, columnOrFooter.fractionDigits || 2);
    case 'date':
      return formatDate(value);
    case 'dateTime':
      return formatDateTime(value, columnOrFooter?.timeProps?.withSeconds);
    case 'currency':
      return value !== undefined && value !== null && formatCurrency(value);
    case 'currencyDecimal':
      return (
          value !== undefined && value !== null && formatCurrency(value, true, columnOrFooter.fractionDigits || 2)
      );
    case 'boolean':
      return value ? 'Ano' : 'Ne';
    default:
      return undefined;
  }
};
